import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { DateTools } from '@treezor/design-system';

import { IPostCreateUser } from '@/features/user/contexts/CreateUserContext';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';
import { useCreateUser } from '@/features/user/hooks/createUser/useCreateUser';
import { useCreateUserKycDocuments } from '@/features/user/hooks/createUser/useCreateUserKycDocuments';

export function useCreateSelfEmployedUser(defaultValues?: IPostCreateUser) {
  const [userId, setUserId] = useState<number | undefined>();

  const {
    handleSubmit,
    language,
    getValues,
    onCreateUser,
    setValue,
    handleValueChange,
    user,
    navigateDashboard,
    isLoading,
    ...rest
  } = useCreateUser(defaultValues);

  const { isTaxResidenceCreated, addTaxResidences, isTaxResidenceLoading } = useAddTaxResidences(userId);
  const { addKycDocuments, isKycDocumentLoading, isKycDocumentsUploaded } = useCreateUserKycDocuments(userId);

  const { watch: watchAddressInfo, setValue: setAddressInfoValue } = useForm({
    defaultValues: { isSameAsBusinessAddress: true },
  });

  const handleIsSameAsBusinessAddressChange = (value: 'yes' | 'no') => {
    setAddressInfoValue('isSameAsBusinessAddress', value === 'yes' ? true : false);
  };

  const isSameAsBusinessAddress = watchAddressInfo('isSameAsBusinessAddress');

  const legalFormValue = { IT: 10001, DE: 20001, ES: 30001 }[user?.distributionCountry] ?? 1000;

  const onConfirmCreation = useCallback(() => {
    const {
      legalRegistrationDate,
      entityType,
      birthday,
      middleName1,
      middleName2,
      taxResidences,
      specifiedUSPerson,
      kycDocuments,
      activityOutsideEu,
      economicSanctions,
      residentCountriesSanctions,
      involvedSanctions,
      ...rest
    } = user;
    const legalRegistrationDateApiFormat = legalRegistrationDate
      ? DateTools.format(DateTools.localStringToDate(legalRegistrationDate, language), 'api-date')
      : undefined;

    const birthdayApiFormat = birthday
      ? DateTools.format(DateTools.localStringToDate(birthday, language), 'api-date')
      : undefined;

    const middleNames = middleName1 || middleName2 ? [middleName1, middleName2].filter(Boolean).join(', ') : '';

    onCreateUser(
      {
        ...rest,
        legalRegistrationDate: legalRegistrationDateApiFormat,
        middleNames,
        birthday: birthdayApiFormat,
        entityType: Number(user.entityType),
        legalForm: legalFormValue.toString(),
        specifiedUSPerson: Number(specifiedUSPerson),
        activityOutsideEu: Number(activityOutsideEu),
        economicSanctions: Number(economicSanctions),
        residentCountriesSanctions: Number(residentCountriesSanctions),
        involvedSanctions: Number(involvedSanctions),
      },
      undefined,
      (userId: number) => {
        setUserId(userId);
        addTaxResidences(taxResidences, userId);
        addKycDocuments(kycDocuments, userId);
      },
    );
  }, [user, language, legalFormValue, onCreateUser, addTaxResidences, addKycDocuments]);

  useEffect(() => {
    if (userId && isTaxResidenceCreated && isKycDocumentsUploaded) {
      navigateDashboard(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isTaxResidenceCreated, isKycDocumentsUploaded]);

  return {
    handleSubmit,
    user,
    language,
    onConfirmCreation,
    getValues,
    isSameAsBusinessAddress,
    handleValueChange,
    handleIsSameAsBusinessAddressChange,
    isLoading: isLoading || isTaxResidenceLoading || isKycDocumentLoading,
    legalFormValue,
    ...rest,
  };
}
